import { formatDates, handleApiResponse } from '@/functions';
import { IFetchDataParams, IEmployee } from '@/interfaces';
import { useQuery } from '@tanstack/react-query';
import { useInvalidateQueries } from '@/hooks';
import { apiClient } from '@/components/api';
import { Paginated } from '@/lib/interface';
import routes from '@/lib/routes';

type TParams = IFetchDataParams & {
  config?: {
    staleTime?: number;
  }
};
type TResponseData = Paginated<IEmployee>;

export const useRequestGetEmployees = (params?: TParams, run = true) => {

  // Default value 1h
  const staleTime = params?.config?.staleTime ?? (60 * 1000 * 60);

  const { invalidate } = useInvalidateQueries();
  const { isFetching, data, error } = useQuery({
    queryKey: [routes.api.employees.base, params, run],
    queryFn: ({ queryKey: [_url, _params, _run] }) =>
      !_run
        ? Promise.resolve(null)
        : fetchQuery(_url as string, _params as TParams),
    staleTime
  });

  const employees = (data?.data ?? []);

  const fetchQuery = (url: string, params?: TParams) =>
    apiClient
      .get<TResponseData>(url, params)
      .then(handleApiResponse)
      .then(data => formatDates(data) as TResponseData);

  const fetchData = (params?: TParams) =>
    fetchQuery(routes.api.employees.base, params);

  const refresh = () =>
    invalidate(routes.api.employees.base);

  return ({
    data: data as TResponseData,
    isLoading: isFetching,
    employees,
    fetchData,
    refresh,
    error
  });

};
